export default class Mat4 {
    mat4: number[];

    constructor (mat = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]) {
        this.mat4 = mat;
    }

    static projection(width: number, height: number, depth: number) {
        return new Mat4 ([
            2 / width, 0, 0, 0,
            0, -2 / height, 0, 0,
            0, 0, 2 / depth, 0,
            -1, 1, 0, 1,
        ])
    }

    static perspective(fov: number, aspect: number, near: number, far: number) {
        const f = 1.0 / Math.tan(fov / 2);
        const rangeInv = 1 / (near - far);
        return new Mat4([
            f / aspect, 0, 0, 0,
            0, f, 0, 0,
            0, 0, (near + far) * rangeInv, -1,
            0, 0, near * far * rangeInv * 2, 0
        ]);
    }

    static translation(tx: number, ty: number, tz: number) {
        return new Mat4 ([
            1,  0,  0,  0,
            0,  1,  0,  0,
            0,  0,  1,  0,
            tx, ty, tz, 1,
        ])
    }

    static xRotation(angleInRadians: number) {
        const c = Math.cos(angleInRadians);
        const s = Math.sin(angleInRadians);
        return new Mat4 ([
            1, 0, 0, 0,
            0, c, s, 0,
            0, -s, c, 0,
            0, 0, 0, 1,
        ])
    }

    static yRotation(angleInRadians: number) {
        const c = Math.cos(angleInRadians);
        const s = Math.sin(angleInRadians);
        return new Mat4 ([
            c, 0, -s, 0,
            0, 1, 0, 0,
            s, 0, c, 0,
            0, 0, 0, 1,
        ])
    };

    static zRotation(angleInRadians: number) {
        const c = Math.cos(angleInRadians);
        const s = Math.sin(angleInRadians);
        return new Mat4 ([
            c, s, 0, 0,
            -s, c, 0, 0,
            0, 0, 1, 0,
            0, 0, 0, 1,
        ])
    }

    static scaling(sx: number, sy: number, sz: number) {
        return new Mat4 ([
            sx, 0,  0,  0,
            0, sy,  0,  0,
            0,  0, sz,  0,
            0,  0,  0,  1,
        ])
    }

    multiply(m: Mat4) {
        return new Mat4([
            m.mat4[0] * this.mat4[0] + m.mat4[1] * this.mat4[4] + m.mat4[2] * this.mat4[8] + m.mat4[3] * this.mat4[12],
            m.mat4[0] * this.mat4[1] + m.mat4[1] * this.mat4[5] + m.mat4[2] * this.mat4[9] + m.mat4[3] * this.mat4[13],
            m.mat4[0] * this.mat4[2] + m.mat4[1] * this.mat4[6] + m.mat4[2] * this.mat4[10] + m.mat4[3] * this.mat4[14],
            m.mat4[0] * this.mat4[3] + m.mat4[1] * this.mat4[7] + m.mat4[2] * this.mat4[11] + m.mat4[3] * this.mat4[15],
            m.mat4[4] * this.mat4[0] + m.mat4[5] * this.mat4[4] + m.mat4[6] * this.mat4[8] + m.mat4[7] * this.mat4[12],
            m.mat4[4] * this.mat4[1] + m.mat4[5] * this.mat4[5] + m.mat4[6] * this.mat4[9] + m.mat4[7] * this.mat4[13],
            m.mat4[4] * this.mat4[2] + m.mat4[5] * this.mat4[6] + m.mat4[6] * this.mat4[10] + m.mat4[7] * this.mat4[14],
            m.mat4[4] * this.mat4[3] + m.mat4[5] * this.mat4[7] + m.mat4[6] * this.mat4[11] + m.mat4[7] * this.mat4[15],
            m.mat4[8] * this.mat4[0] + m.mat4[9] * this.mat4[4] + m.mat4[10] * this.mat4[8] + m.mat4[11] * this.mat4[12],
            m.mat4[8] * this.mat4[1] + m.mat4[9] * this.mat4[5] + m.mat4[10] * this.mat4[9] + m.mat4[11] * this.mat4[13],
            m.mat4[8] * this.mat4[2] + m.mat4[9] * this.mat4[6] + m.mat4[10] * this.mat4[10] + m.mat4[11] * this.mat4[14],
            m.mat4[8] * this.mat4[3] + m.mat4[9] * this.mat4[7] + m.mat4[10] * this.mat4[11] + m.mat4[11] * this.mat4[15],
            m.mat4[12] * this.mat4[0] + m.mat4[13] * this.mat4[4] + m.mat4[14] * this.mat4[8] + m.mat4[15] * this.mat4[12],
            m.mat4[12] * this.mat4[1] + m.mat4[13] * this.mat4[5] + m.mat4[14] * this.mat4[9] + m.mat4[15] * this.mat4[13],
            m.mat4[12] * this.mat4[2] + m.mat4[13] * this.mat4[6] + m.mat4[14] * this.mat4[10] + m.mat4[15] * this.mat4[14],
            m.mat4[12] * this.mat4[3] + m.mat4[13] * this.mat4[7] + m.mat4[14] * this.mat4[11] + m.mat4[15] * this.mat4[15]
        ])
    }

    translate(tx: number, ty: number, tz: number) {
        const mat = Mat4.translation(tx, ty, tz);
        return this.multiply(mat);
    }

    xRotate(angleInRadians: number) {
        const mat = Mat4.xRotation(angleInRadians);
        return this.multiply(mat);
    }

    yRotate(angleInRadians: number) {
        const mat = Mat4.yRotation(angleInRadians);
        return this.multiply(mat);
    }

    zRotate(angleInRadians: number) {
        const mat = Mat4.zRotation(angleInRadians);
        return this.multiply(mat);
    } 

    rotate(rx: number = 0, ry: number = 0, rz: number = 0) {
        let mat = this.xRotate(rx);
        mat = mat.yRotate(ry);
        mat = mat.zRotate(rz);
        return mat
    }

    scale(sx: number, sy: number, sz: number) {
        const mat = Mat4.scaling(sx, sy, sz);
        return this.multiply(mat);
    }

    transpose() {
        const newMat = Array(16);
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                newMat[j*4+i] = this.mat4[i*4+j];
            }
        }
        return new Mat4(newMat);
    }

    inverse() {
        let temp;
        const oldMat = [...this.mat4];
        const newMat = new Mat4();
        const mat = newMat.mat4;
        for (var k = 0; k < 4; k++) {
            temp = oldMat[k*4+k];
            for (var j = 0; j < 4; j++) {
                oldMat[k*4+j] /= temp;
                mat[k*4+j] /= temp;
            }
            for (var i = k + 1; i < 4; i++) {
                temp = oldMat[i*4+k];
                for (var j = 0; j < 4; j++) {
                    oldMat[i*4+j] -= oldMat[k*4+j] * temp;
                    mat[i*4+j] -= mat[k*4+j] * temp;
                }
            }
        }
        for (var k = 4 - 1; k > 0; k--) {
            for (var i = k - 1; i >= 0; i--) {
                temp = oldMat[i*4+k];
                for (var j = 0; j < 4; j++) {
                    oldMat[i*4+j] -= oldMat[k*4+j] * temp;
                    mat[i*4+j] -= mat[k*4+j] * temp;
                }
            }
        }
        return newMat;
    }
}